import { action, flow, getParentOfType, register, types, view } from "@gadgetinc/mobx-quick-tree";
import { toaster } from "baseui/toast";
import { isNil } from "lodash";
import { GadgetDomainModelClass } from "state-trees/src/utils";

export const ChatRole = types.enumeration("Role", ["user", "system"]);
const PREAMBLE_END = "PREAMBLE_END";
const errorAnswer = "Sorry, there was an error with the chatbot. Please try again later.";

/** Represents one message from either party in the chat */
@register
export class ChatMessage extends GadgetDomainModelClass("ChatMessage", {
  role: ChatRole,
  message: types.string,
  loading: types.optional(types.boolean, false),
  isError: types.optional(types.boolean, false),
  isSavingRating: types.optional(types.boolean, false),
  userRating: types.maybeNull(types.boolean),
  userRatingComment: types.maybeNull(types.string),
  references: types.array(types.frozen<{ url: string; title: string }>()),
}) {
  get rateable() {
    return this.role === "system" && !this.loading && !this.isError;
  }

  get showReferences() {
    return !this.loading && this.references.length > 0;
  }

  @action
  setError(errorMessage = errorAnswer) {
    this.isError = true;
    this.message = errorMessage;
  }
  @action
  setMessage(message: string) {
    this.message = message;
  }
  @action
  appendMessage(chunk: string) {
    this.message += chunk;
  }
  @action
  setUserRating(isGood: boolean) {
    this.userRating = isGood;
  }
  @action
  setUserRatingComment(comment: string) {
    this.userRatingComment = comment;
  }
  @action
  setReferences(references: { url: string; title: string }[]) {
    this.references.replace(references);
  }

  @action
  saveRating = flow(function* (this: ChatMessage) {
    if (isNil(this.userRating)) return;
    this.isSavingRating = true;
    try {
      const res = yield fetch(`${(window as any).GadgetDocsConfig.servicesRootURL}/chat/rate-message`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          conversationKey: getParentOfType(this, ChatbotSession).key,
          messageKey: this.key,
          isGood: this.userRating,
          comment: this.userRatingComment,
        }),
      });

      if (!res.ok || !res.body) {
        toaster.negative("There was an error submitting your rating, please try again", { autoHideDuration: 2000 });
        return;
      }
    } finally {
      this.isSavingRating = false;
    }
  });
}

/** State manager for a session with the chat backend */
@register
export class ChatbotSession extends GadgetDomainModelClass("ChatbotSession", {
  messages: types.array(ChatMessage),
  docsVersion: types.maybe(types.string),
  prompt: types.optional(types.string, ""),
  started: types.optional(types.boolean, false),
  loading: types.optional(types.boolean, false),
  error: types.maybe(types.string),
}) {
  canSubmit() {
    return this.prompt != "";
  }

  @action
  setPrompt(input: string) {
    this.prompt = input;
  }

  @action
  submitPrompt = flow(function* (this: ChatbotSession) {
    if (this.prompt == "") return;
    this.loading = true;
    this.started = true;
    const prompt = this.prompt;
    this.prompt = "";
    const promptMessage = ChatMessage.create({ role: "user", message: prompt });
    this.messages.push(promptMessage);
    const nextMessage = ChatMessage.create({ role: "system", message: "", loading: true });
    this.messages.push(nextMessage);

    try {
      const res: Response = yield fetch(`${(window as any).GadgetDocsConfig.servicesRootURL}/chat/message`, {
        credentials: "include",
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          docsVersion: this.docsVersion,
          conversationKey: this.key,
          promptMessageKey: promptMessage.key,
          responseMessageKey: nextMessage.key,
          prompt,
        }),
      });

      if (!res.ok || !res.body) {
        nextMessage.setError();
        return;
      }

      const stream = res.body.pipeThrough(new TextDecoderStream()).getReader();
      let done = false;
      const { preamble, extra } = yield readPreamble(stream);
      nextMessage.setReferences(preamble.references);
      nextMessage.appendMessage(extra);

      while (!done) {
        const { value, done: doneReading } = yield stream.read();
        done = doneReading;
        if (value) {
          nextMessage.appendMessage(value);
        }
      }
    } catch (e) {
      console.error("[chat] Error processing chatbot response stream", e);
      nextMessage.setError(errorAnswer);
    } finally {
      nextMessage.loading = false;
      this.loading = false;
    }
  });
}

/**
 * The stream from the server includes a JSON preamble at the start of the string, then the string PREAMBLE_END, then the stream from the chatbot
 * Read the preamble and JSON parse it, and return any extra content read in
 **/
const readPreamble = async (reader: ReadableStreamDefaultReader<string>) => {
  let done = false;
  let buffer = "";
  while (!buffer.includes(PREAMBLE_END) && !done) {
    const { value, done: doneReading } = await reader.read();
    done = doneReading;
    buffer += value;
  }

  const preamble = buffer.slice(0, buffer.indexOf(PREAMBLE_END));
  const extra = buffer.slice(buffer.indexOf(PREAMBLE_END) + PREAMBLE_END.length);
  return { preamble: JSON.parse(preamble), extra };
};

@register
// eslint-disable-next-line jsdoc/require-jsdoc
export class CodeGenSession extends ChatbotSession {
  abortController: AbortController | undefined | null;

  @view
  getLatestMessage() {
    return this.messages[this.messages.length - 1]?.message;
  }

  @action
  abort() {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }

    this.loading = false;
  }

  @action
  submitCodeGenPrompt = flow(function* (this: CodeGenSession, filePath: string) {
    if (this.prompt == "") {
      return;
    }

    this.abortController = new AbortController();

    this.loading = true;
    this.started = true;
    const prompt = this.prompt;
    const promptMessage = ChatMessage.create({ role: "user", message: prompt });
    const nextMessage = ChatMessage.create({ role: "system", message: "", loading: true });
    this.messages.push(nextMessage);

    try {
      const res: Response = yield fetch(`/edit/api/codegen/generate`, {
        signal: this.abortController.signal,
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          prompt,
          filePath: filePath,
          conversationKey: this.key,
          promptMessageKey: promptMessage.key,
          responseMessageKey: nextMessage.key,
        }),
      });

      if (!res.ok || !res.body) {
        nextMessage.setError();
        return;
      }

      const stream = res.body.pipeThrough(new TextDecoderStream()).getReader();
      let done = false;

      while (!done) {
        const { value, done: doneReading } = yield stream.read();
        done = doneReading;
        if (value) {
          nextMessage.appendMessage(value);
        }
      }
    } catch (e) {
      console.error("[chat] Error processing chatbot response stream", e);
      nextMessage.setError(errorAnswer);
    } finally {
      this.abortController = null;
      nextMessage.loading = false;
      this.loading = false;
    }
  });
}
